import { styled } from '@mui/material/styles';
import { Box, Paper, Card, Button, IconButton, Typography } from '@mui/material';

// Honey-inspired color palette
export const honeyColors = {
  goldenHoney: '#F1C40F',
  brightHoney: '#F4D03F',
  honeycomb: '#D4A574',
  darkHoney: '#B8860B',
  honeyBrown: '#5D4E37',
  softCream: '#FFFEF7',
  warmAmber: '#CD853F',
  lightBrown: '#8B7355',
  honeyGradient: 'linear-gradient(135deg, #F1C40F, #F4D03F)',
  honeycombGradient: 'linear-gradient(135deg, #D4A574, #F4D03F)',
  creamGradient: 'linear-gradient(135deg, #FFF8DC, #FFFACD)',
};

// Main container with honey-inspired styling
export const HoneyContainer = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: honeyColors.honeyBrown,
  maxWidth: '800px',
  margin: '0 auto',
}));

// Header section (similar to services)
export const HoneyGalleryHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(3),
}));

// Title with honey gradient (similar to services)
export const HoneyGalleryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  background: honeyColors.honeycombGradient,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

// Subtitle (similar to services)
export const HoneyGallerySubtitle = styled(Typography)(({ theme }) => ({
  color: honeyColors.lightBrown,
  fontSize: '1.1rem',
  fontWeight: 400,
}));

// Image count styling
export const ImageCount = styled(Typography)(({ theme }) => ({
  color: honeyColors.honeyBrown,
  fontWeight: 700,
  fontSize: '1.1rem',
  textShadow: '0 1px 2px rgba(255, 255, 255, 0.5)',
}));

// Honey-styled buttons
export const HoneyButton = styled(Button)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  color: honeyColors.honeyBrown,
  border: `2px solid ${honeyColors.honeycomb}60`,
  borderRadius: '50px',
  fontWeight: 600,
  fontSize: '1rem',
  padding: theme.spacing(1, 2.5),
  transition: 'all 0.3s ease',
  backdropFilter: 'blur(10px)',
  textTransform: 'none',
  
  '&:hover': {
    background: 'rgba(255, 255, 255, 1)',
    borderColor: honeyColors.honeycomb,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 12px ${honeyColors.honeycomb}30`,
  },
  
  '&.primary': {
    background: honeyColors.honeyGradient,
    color: honeyColors.honeyBrown,
    border: 'none',
    boxShadow: `0 8px 32px ${honeyColors.goldenHoney}50`,
    
    '&:hover': {
      background: `linear-gradient(135deg, ${honeyColors.brightHoney}, ${honeyColors.goldenHoney})`,
      transform: 'translateY(-4px)',
      boxShadow: `0 12px 48px ${honeyColors.goldenHoney}70`,
    },
  },
}));

// Drop zone styling
export const HoneyDropZone = styled(Box)(({ theme }) => ({
  border: `3px dashed ${honeyColors.honeycomb}`,
  borderRadius: '16px',
  padding: theme.spacing(2),
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${honeyColors.brightHoney}10, ${honeyColors.honeycomb}10)`,
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  minHeight: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  
  '&:hover, &.dragover': {
    borderColor: honeyColors.goldenHoney,
    background: `linear-gradient(135deg, ${honeyColors.brightHoney}20, ${honeyColors.goldenHoney}15)`,
    transform: 'scale(1.02)',
    boxShadow: `0 8px 25px ${honeyColors.brightHoney}30`,
  },
}));

// Gallery container with grid layout
export const HoneyGallery = styled(Paper)(({ theme }) => ({
  background: honeyColors.softCream,
  borderRadius: '24px',
  padding: theme.spacing(3),
  border: `1px solid ${honeyColors.brightHoney}30`,
  boxShadow: `0 8px 32px ${honeyColors.honeyBrown}12`,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: theme.spacing(2),
  
  // Mobile responsive
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: theme.spacing(1.5),
  },
  
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(1.5),
  },
}));

// Individual image card styling
export const HoneyImageCard = styled(Card)(({ theme, isNew = false }) => ({
  background: isNew ? `${honeyColors.brightHoney}15` : honeyColors.softCream,
  border: `3px solid ${isNew ? honeyColors.goldenHoney : 'transparent'}`,
  borderRadius: '24px',
  overflow: 'hidden',
  boxShadow: `0 8px 32px ${honeyColors.honeyBrown}12`,
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  cursor: 'grab',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: 'fit-content',
  
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 60px ${honeyColors.honeycomb}25`,
    borderColor: honeyColors.goldenHoney,
  },
  
  '&:active': {
    cursor: 'grabbing',
    transform: 'rotate(2deg) scale(1.02)',
  },
  
  // Honey accent stripe
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(135deg, ${honeyColors.goldenHoney} 0%, ${honeyColors.brightHoney} 50%, ${honeyColors.honeycomb} 100%)`,
    borderRadius: '24px 24px 0 0',
    zIndex: 1,
  },
  
  // Hexagon accent
  '&::after': {
    content: '""',
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    width: '10px',
    height: '10px',
    background: honeyColors.goldenHoney,
    clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
    zIndex: 1,
  },
}));

// Image content area
export const HoneyImageContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  flex: 1,
}));

// Image container for the actual image
export const HoneyImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '200px',
  overflow: 'hidden',
  cursor: 'pointer',
  
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  
  '&:hover img': {
    transform: 'scale(1.05)',
  },
}));

// Position controls container
export const HoneyPositionControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

// Position display with honey styling
export const HoneyPositionDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  background: honeyColors.creamGradient,
  padding: theme.spacing(0.5, 1),
  borderRadius: '20px',
  border: `2px solid ${honeyColors.brightHoney}`,
}));

// Position buttons with honey styling
export const HoneyPositionButton = styled(IconButton)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  color: honeyColors.honeycomb,
  transition: 'all 0.2s ease',
  
  '&:hover': {
    background: honeyColors.honeycomb,
    color: honeyColors.honeyBrown,
    transform: 'scale(1.1)',
  },
  
  '&:disabled': {
    color: '#DDD',
    cursor: 'not-allowed',
    
    '&:hover': {
      background: 'none',
      transform: 'none',
    },
  },
}));

// Position number styling
export const HoneyPositionNumber = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: honeyColors.darkHoney,
  minWidth: '24px',
  textAlign: 'center',
}));

// Action buttons container
export const HoneyActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  justifyContent: 'center',
  alignItems: 'center',
  borderTop: `1px solid ${honeyColors.brightHoney}30`,
  backgroundColor: 'rgba(255, 255, 255, 0.5)',
}));

// Individual action button
export const HoneyActionButton = styled(IconButton)(({ theme, variant = 'default' }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'drag':
        return {
          background: `linear-gradient(135deg, ${honeyColors.honeycomb}, ${honeyColors.goldenHoney})`,
          color: honeyColors.honeyBrown,
          border: 'none',
          
          '&:hover': {
            background: `linear-gradient(135deg, ${honeyColors.goldenHoney}, ${honeyColors.brightHoney})`,
          },
        };
      case 'delete':
        return {
          color: honeyColors.warmAmber,
          border: `2px solid ${honeyColors.warmAmber}`,
          
          '&:hover': {
            background: honeyColors.warmAmber,
            color: 'white',
          },
        };
      default:
        return {
          border: `2px solid ${honeyColors.brightHoney}`,
          color: honeyColors.honeycomb,
          
          '&:hover': {
            background: honeyColors.brightHoney,
            color: honeyColors.honeyBrown,
          },
        };
    }
  };

  return {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    ...getVariantStyles(),
    
    '&:hover': {
      transform: 'scale(1.1)',
      ...getVariantStyles()['&:hover'],
    },
  };
});

// Empty state styling
export const HoneyEmptyState = styled(Box)(({ theme }) => ({
  height: '200px',
  border: `3px dashed ${honeyColors.honeycomb}`,
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  background: `linear-gradient(135deg, ${honeyColors.brightHoney}10, ${honeyColors.honeycomb}10)`,
  color: honeyColors.honeyBrown,
}));

// Image preview overlay
export const HoneyPreviewOverlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(93, 78, 55, 0.85)', // Honey brown with transparency
  zIndex: 1300,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  backdropFilter: 'blur(8px)',
}));

// Description text styling
export const HoneyDescription = styled(Typography)(({ theme }) => ({
  color: '#8B7355', // Warm honey brown
  lineHeight: 1.5,
  marginBottom: theme.spacing(3),
}));

// Subtitle styling
export const HoneySubtitle = styled(Typography)(({ theme }) => ({
  color: '#8B7355',
  fontWeight: 500,
  fontSize: '0.9rem',
}));

// Image name/alt text styling
export const HoneyImageText = styled(Typography)(({ theme }) => ({
  color: '#8B7355',
  fontSize: '0.95rem',
  lineHeight: 1.4,
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));